import { C } from '@parsina/framework/tokens';
import { brand, BRAND_DEFAULTS } from '@parsina/framework/ui/lib/brand';
import { fmtCoins } from '@parsina/framework/clinical/src/constants/creditPricing';
import { creditActionLabel } from '../../lib/ledgerVocabulary';
import { hasLocalLedgerString, localLedgerString } from './ledgerStrings';

// ── Service identity ──────────────────────────────────────────────────────────
// Brand semantics come from tokens; purple/blue are per-service identity accents
// with no brand token, kept as this screen's local service palette.
const PURPLE = '#7C3AED', PURPLE_BG = '#F5F3FF', BLUE = '#3B82F6', BLUE_BG = '#EFF6FF';

export const SERVICE_META = (t) => ({
  AI_TRANSCRIPTION:     { label: t('credits.service.transcription'),       color: C.teal,  bg: C.tealLight,  icon: MicIcon },
  AI_LLM_SUMMARY:       { label: t('credits.service.summary'),        color: PURPLE,  bg: PURPLE_BG,    icon: SparkIcon },
  AI_INTERACTION_CHECK: { label: t('credits.price.interaction'), color: C.amber, bg: C.amberLight, icon: ShieldIcon },
  TOP_UP:               { label: t('nav.credits.topUp'),         color: C.green, bg: C.greenLight, icon: PlusIcon },
  SMS:                  { label: t('auth.sms'),               color: BLUE,    bg: BLUE_BG,      icon: ChatIcon },
});
export const DEFAULT_META = (t) => ({ label: t('credits.service.ai'), color: PURPLE, bg: PURPLE_BG, icon: SparkIcon });

// Old rows stored the proto enum integer ("2","3","4").
const SERVICE_TYPE_INT_MAP = { '2': 'AI_TRANSCRIPTION', '3': 'AI_LLM_SUMMARY', '4': 'AI_INTERACTION_CHECK' };

export function serviceKey(rawType) {
  const resolved = SERVICE_TYPE_INT_MAP[rawType] || rawType;
  return (resolved || '').replace(/^SERVICE_TYPE_/, '');
}
export function getServiceMeta(rawType, t) {
  return SERVICE_META(t)[serviceKey(rawType)] || DEFAULT_META(t);
}

// ── Description parsing ───────────────────────────────────────────────────────
export function parseDescription(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return raw; }
}

/** { title, sub, visitId } — title = human action, sub = model/tokens/duration. */
export function parseTransactionLabels(raw, t) {
  const parsed = parseDescription(raw);
  if (!parsed || typeof parsed === 'string') return { title: parsed || '', sub: '', visitId: null };

  // Token counts and the model name are recorded on the transaction (the AI
  // service sends input_tokens / output_tokens / model with every deduction, and
  // they stay in the ledger for cost analysis) but they are deliberately NOT
  // shown here. They are our unit economics, not the doctor's: a doctor reading
  // «۳۲٬۰۰۰ توکن · gemini-2.5-pro» learns nothing they can act on, and it
  // invites questions about which model ran rather than what the visit cost.
  // The doctor sees what they spent, on what, and when.
  const sub = [];
  if (parsed.duration_seconds) sub.push(t('credits.minutesUsed', { count: Math.ceil(parsed.duration_seconds / 60) }));
  // `parsed.action` is written by the AI service and is PERSIAN in every
  // language — «رونویسی صوتی», «خلاصه ویزیت», «بررسی تداخل دارویی». It is a
  // closed vocabulary, so it is translated here at the display edge; the stored
  // value is untouched, and an action this app has not learned about renders as
  // itself rather than as a blank row.
  return {
    title: creditActionLabel(parsed.action, t),
    sub: sub.join(' · '),
    visitId: parsed.visit_id || parsed.patient_id || null,
  };
}

// ── Lines named by their reason code ──────────────────────────────────────────
// A credit the platform or a payment puts on the ledger carries a reason code
// (`serviceType`) beside a sentence its writer composed at the time — Persian,
// and naming Parsina's credit: «هدیه ثبت‌نام», «خرید 500 پارسه از bazaar»,
// «کد هدیه …», «جایزهٔ گردونه …», «sepehr top-up: شارژ ۲۰۰ پارسه». Stored text
// is history and is never rewritten (docs/rebranding/CONTRACT.md §1), so a
// deployment of another brand names these lines from the reason code instead:
// in the reader's language, with its own credit's name. With no brand
// configured the stored sentence is shown exactly as it always was, in every
// language — Parsina does not change.
//
//   SIGNUP_GRANT    the opening grant
//   TOP_UP          a gateway payment, of the row's amount
//   STORE_<STORE>   an in-app purchase, of the row's amount, from that store
//   GIFT_CODE       a prize (reference `prize:<draw>`), an operator's grant
//                   (`manual-grant-…`, or none), else a gift code — whose
//                   reference IS the code
//
// Every other line — the AI service's JSON, a code this does not know — is read
// from what is stored, as before.
function reasonLine(txn) {
  if (txn?.type !== 'credit') return null;
  const code = String(txn.serviceType || '');
  const ref = String(txn.referenceId || '');
  if (code === 'SIGNUP_GRANT') return { key: 'credits.ledger.signupGift' };
  if (code === 'TOP_UP') return { key: 'credits.ledger.topUp', amount: true };
  if (/^STORE_[A-Z0-9_]+$/.test(code)) {
    return { key: 'credits.ledger.storePurchase', amount: true, store: code.slice('STORE_'.length).toLowerCase() };
  }
  if (code === 'GIFT_CODE') {
    if (ref.startsWith('prize:')) return { key: 'credits.ledger.prize' };
    if (!ref || ref.startsWith('manual-grant-')) return { key: 'credits.ledger.gift' };
    return { key: 'credits.ledger.giftCode', code: ref };
  }
  return null;
}

// `brand()` hands back BRAND_DEFAULTS itself for as long as nothing is
// configured (ui/lib/brand.js): that identity is "Parsina, nothing set".
const brandConfigured = () => brand() !== BRAND_DEFAULTS;

/**
 * { title, sub, visitId } for one ledger row — `parseTransactionLabels` of its
 * stored description, or, under a configured brand, the name of its reason code
 * (above). `i18n` is what `useTranslation()` returns.
 */
export function transactionLabels(txn, { t, has, locale, formatNumber }) {
  const reason = brandConfigured() ? reasonLine(txn) : null;
  if (!reason) return parseTransactionLabels(txn?.description, t);
  // The kit's catalogue first (and with it the brand's own overlay), this
  // app's ledger table otherwise (./ledgerStrings.js).
  const say = (key, vars) => (has(key) || !hasLocalLedgerString(key) ? t(key, vars) : localLedgerString(locale, key, vars));
  const vars = {};
  if (reason.amount) vars.amount = fmtCoins(txn.amount, formatNumber);
  if (reason.store) {
    const storeKey = `credits.ledger.stores.${reason.store}`;
    vars.store = has(storeKey) || hasLocalLedgerString(storeKey) ? say(storeKey) : reason.store;
  }
  if (reason.code) vars.code = reason.code;
  return { title: say(reason.key, vars), sub: '', visitId: null };
}

// ── Date helpers ──────────────────────────────────────────────────────────────
// Named `txnTime`, not `formatTime`, so it cannot be confused with — or shadow —
// the `formatTime` the hook hands out, which is what it now delegates to. Takes
// the formatter as an argument like its sibling `dayLabel` below: this is a
// plain module and cannot call a hook.
//
// It used to build the string itself with `toLocaleTimeString('fa-IR')`, which
// was Persian digits in every language AND the browser's time zone rather than
// the clinic's. The hook's formatter pins Asia/Tehran and 24-hour, which is the
// clock the doctor is actually reading.
export function txnTime(isoStr, formatTime) {
  if (!isoStr) return '';
  try { return formatTime(isoStr); }
  catch { return ''; }
}
function dayKey(isoStr) {
  const d = new Date(isoStr);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function dayLabel(isoStr, t, formatDate) {
  const d = new Date(isoStr);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return t('common.time.today');
  if (diffDays === 1) return t('common.time.yesterday');
  try { return formatDate(d, 'full'); }
  catch { return isoStr; }
}

/** Group a (chronological) list of transactions into [{ key, label, items }]. */
export function groupByDay(transactions, t, formatDate) {
  const groups = [];
  const index = {};
  transactions.forEach(item => {
    const k = dayKey(item.createdAt);
    if (index[k] == null) {
      index[k] = groups.length;
      groups.push({ key: k, label: dayLabel(item.createdAt, t, formatDate), items: [] });
    }
    groups[index[k]].items.push(item);
  });
  return groups;
}

/** Group transactions by service TYPE → [{ key, meta, total, items }], desc by |total|. */
export function groupByType(transactions, t) {
  const map = {};
  transactions.forEach(item => {
    const key = serviceKey(item.serviceType);
    if (!map[key]) map[key] = { key, meta: getServiceMeta(key, t), total: 0, items: [] };
    map[key].total += item.type === 'debit' ? item.amount : -item.amount; // net (debits +, credits −)
    map[key].items.push(item);
  });
  return Object.values(map).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
}

// ── Date-range presets → ISO bounds ─────────────────────────────────────────────
// The wallet filters `created_at >= start_date` / `<= end_date` (Postgres casts the
// ISO string). End bounds are pushed to 23:59:59 so the whole end-day is inclusive.
export const DATE_PRESETS = (t) => ([
  { id: 'all',   label: t('common.state.all') },
  { id: 'today', label: t('common.time.today') },
  { id: '7d',    label: t('patient.analysis.days7') },
  { id: '30d',   label: t('credits.range.days30') },
  { id: 'month', label: t('common.time.thisMonth') },
]);

const pad = (n) => String(n).padStart(2, '0');
const startISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
const endISO   = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59:59`;

/** Resolve a preset (or custom 'YYYY-MM-DD' bounds) to { startDate, endDate } ISO strings. */
export function rangeToISO(preset, custom = {}) {
  const now = new Date();
  if (preset === 'custom') {
    if (!custom.from || !custom.to) return { startDate: '', endDate: '' };
    return { startDate: `${custom.from}T00:00:00`, endDate: `${custom.to}T23:59:59` };
  }
  switch (preset) {
    case 'today':
      return { startDate: startISO(now), endDate: endISO(now) };
    case '7d': {
      const from = new Date(now); from.setDate(now.getDate() - 6);
      return { startDate: startISO(from), endDate: endISO(now) };
    }
    case '30d': {
      const from = new Date(now); from.setDate(now.getDate() - 29);
      return { startDate: startISO(from), endDate: endISO(now) };
    }
    case 'month':
      return { startDate: startISO(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: endISO(now) };
    default: // 'all'
      return { startDate: '', endDate: '' };
  }
}

// ── KPI computation ───────────────────────────────────────────────────────────
/**
 * From the loaded transactions, derive: this-month spend, personal per-visit
 * average (distinct visit_id in the description), and the top service this month.
 * All client-side over the loaded page(s) — replace with a server metric later.
 */
export function computeStats(transactions) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

  let usedThisMonth = 0, totalCredited = 0, monthDebitTotal = 0;
  const byService = {};
  const visitSpend = {};

  transactions.forEach(item => {
    const at = new Date(item.createdAt).getTime();
    if (item.type === 'debit') {
      const key = serviceKey(item.serviceType);
      if (at >= monthStart) {
        usedThisMonth += item.amount;
        monthDebitTotal += item.amount;
        byService[key] = (byService[key] || 0) + item.amount;
      }
      const parsedDesc = parseDescription(item.description);
      const visitId = (parsedDesc && typeof parsedDesc === 'object')
        ? (parsedDesc.visit_id || parsedDesc.patient_id || null) : null;
      if (visitId) visitSpend[visitId] = (visitSpend[visitId] || 0) + item.amount;
    } else {
      totalCredited += item.amount;
    }
  });

  const visitIds = Object.keys(visitSpend);
  const avgPerVisit = visitIds.length
    ? visitIds.reduce((s, k) => s + visitSpend[k], 0) / visitIds.length
    : null;

  let topService = null, topAmount = 0;
  Object.entries(byService).forEach(([k, v]) => { if (v > topAmount) { topAmount = v; topService = k; } });
  const topPct = monthDebitTotal ? Math.round((topAmount / monthDebitTotal) * 100) : 0;

  return { usedThisMonth, totalCredited, avgPerVisit, topService, topPct, byService, monthDebitTotal };
}

/** Per-service breakdown rows (debits, all loaded), sorted desc. */
export function serviceBreakdown(transactions, t) {
  const map = {};
  transactions.forEach(item => {
    if (item.type !== 'debit') return;
    const key = serviceKey(item.serviceType);
    if (!map[key]) map[key] = { amount: 0, count: 0, meta: getServiceMeta(key, t) };
    map[key].amount += item.amount;
    map[key].count += 1;
  });
  return Object.entries(map).map(([key, v]) => ({ key, ...v })).sort((a, b) => b.amount - a.amount);
}

// ── SVG icons (shared) ────────────────────────────────────────────────────────
export function MicIcon({ size = 16, color = 'currentColor' }) {
  return <svg viewBox="0 0 24 24" fill={color} width={size} height={size}><path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/></svg>;
}
export function SparkIcon({ size = 16, color = 'currentColor' }) {
  return <svg viewBox="0 0 24 24" fill={color} width={size} height={size}><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>;
}
export function ShieldIcon({ size = 16, color = 'currentColor' }) {
  return <svg viewBox="0 0 24 24" fill={color} width={size} height={size}><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 13l-3-3 1.41-1.41L11 11.17l4.59-4.58L17 8l-6 6z"/></svg>;
}
export function PlusIcon({ size = 16, color = 'currentColor' }) {
  return <svg viewBox="0 0 24 24" fill={color} width={size} height={size}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/></svg>;
}
export function ChatIcon({ size = 16, color = 'currentColor' }) {
  return <svg viewBox="0 0 24 24" fill={color} width={size} height={size}><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>;
}
